/* Subcontractor Mgmt (Duncan 2026-10-20): the lists and rules its pages share. The trades, standings and
   document kinds mirror server/routes/subcontractors.js. */

// One fixed list, shared with the subcontractor portal (each trade sets a job layout there): the portal's
// six, Duncan's five from the CE subcontract list (2026-10-20), then Other.
export const TRADES = ['Lighting', 'HVAC', 'Controls', 'Water', 'Electrical', 'Solar',
  'Roofing', 'Fire Alarm', 'Windows', 'Envelope', 'Storage', 'Other'];

export const STANDINGS = [
  { value: 'approved', label: 'Approved', tone: 'var(--success)', soft: 'var(--success-soft)' },
  { value: 'probation', label: 'Probation', tone: 'var(--warning)', soft: 'var(--warning-soft)' },
  { value: 'do_not_use', label: 'Do not use', tone: 'var(--danger)', soft: 'var(--danger-soft)' },
];
export const standingOf = (value) => STANDINGS.find((s) => s.value === value) || STANDINGS[0];

/* The kinds of company document, in the order the Documents tab lists them, and which dates each one asks for.
   `always`: the group shows even when empty ("None on file"). A certificate of insurance and a safety plan
   belong to a job now (JOB_DOC_KINDS); the general ones here are kept, not required. */
export const DOC_TYPES = [
  { value: 'msa', label: 'MSA', group: 'Master service agreement', title: 'Master service agreement', dates: ['signed_date', 'effective_date', 'expiry_date'], always: true },
  { value: 'w9', label: 'W-9', group: 'W-9', title: 'W-9', dates: ['signed_date'], always: true },
  { value: 'prequal', label: 'Prequalification questionnaire', group: 'Prequalification questionnaire', title: 'Prequalification questionnaire', dates: ['signed_date'] },
  { value: 'license', label: 'License', group: 'Licenses', title: 'License', number: 'License #', dates: ['expiry_date'] },
  { value: 'insurance', label: 'Insurance certificate (general, not a job’s)', group: 'Insurance certificates (general)', title: 'Insurance certificate', number: 'Policy #', dates: ['effective_date', 'expiry_date'] },
  { value: 'safety', label: 'Safety (general, not a job’s)', group: 'Safety (general)', title: 'Safety', dates: ['expiry_date'] },
  { value: 'other', label: 'Other', group: 'Other documents', title: 'Document', dates: ['expiry_date'] },
];

/* A job's documents (Duncan 2026-10-22, from the construction administration call): "for each job, we require a
   certificate of insurance" and "a job site safety plan ... specific to the job" -- every sub on every job. */
export const JOB_DOC_KINDS = [
  { value: 'insurance', label: 'Certificate of insurance', title: 'Certificate of insurance', number: 'Policy #', dates: ['effective_date', 'expiry_date'], short: 'COI' },
  { value: 'safety', label: 'Job site safety plan', title: 'Job site safety plan', dates: [], short: 'safety plan' },
  // The executed subcontract (Duncan 2026-10-29): added with its amount, which the sub's Billing tab builds on.
  { value: 'subcontract', label: 'Subcontract', title: 'Subcontract', number: 'CE Sub #', dates: ['signed_date'], short: 'subcontract' },
];
export const jobDocKindOf = (value) => JOB_DOC_KINDS.find((k) => k.value === value) || JOB_DOC_KINDS[0];

/* What a job's certificate shows (the construction administrator's paragraph, Duncan 2026-10-22): the job named --
   its name, job number or owner, in the description box or as a certificate holder -- is the must; the rest is what
   E3 asks for, followed up when missing. Summit reads each certificate for them by keyword (server/coiScan.js);
   `short` is how a missing one is said. */
export const COI_CHECKS = [
  { key: 'names_job', label: 'Names this job', hint: 'its name, job number or the owner, in the description or as a certificate holder' },
  { key: 'additional_insured', label: 'E3 and the owner additional insured', short: 'additional insured' },
  { key: 'primary_noncontributory', label: 'Primary and non-contributory', short: 'primary and non-contributory' },
  { key: 'waiver_of_subrogation', label: 'Waiver of subrogation', short: 'waiver of subrogation' },
  { key: 'notice_30_days', label: '30 days’ notice of cancellation', short: '30 days’ notice' },
  { key: 'follow_form', label: 'Umbrella / excess follow form', short: 'follow form' },
];
const FOLLOW_UPS = COI_CHECKS.filter((c) => c.short);

/** A job that's done needs nothing more: archived, Complete or Closed. */
export const jobNeedsDocs = (job) => !!job && !job.archived && !['Complete', 'Closed'].includes(job.status);
export const docTypeOf = (value) => DOC_TYPES.find((t) => t.value === value) || DOC_TYPES[DOC_TYPES.length - 1];
export const DATE_LABEL = { signed_date: 'Signed', effective_date: 'Effective', expiry_date: 'Expires' };

/* Money for a sub's subcontract and schedule of values (2026-10-29), kept in whole cents. */
/** Cents from what was typed -- "$32,083.00", "32083", "1,500.5" -- or null when it isn't an amount. */
export const centsFromText = (text) => {
  const t = String(text == null ? '' : text).replace(/[$,\s]/g, '');
  if (!/^\d+(\.\d{0,2})?$|^\.\d{1,2}$/.test(t)) return null;
  const [whole, frac = ''] = t.split('.');
  return Number(whole || 0) * 100 + Number((frac + '00').slice(0, 2));
};
/** "$32,083.00" from cents. */
export const moneyOfCents = (cents) => `$${(Number(cents || 0) / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const pad = (n) => String(n).padStart(2, '0');
/** Today, 'YYYY-MM-DD', on this computer's calendar. */
export const todayYmd = () => {
  const t = new Date();
  return `${t.getFullYear()}-${pad(t.getMonth() + 1)}-${pad(t.getDate())}`;
};
/** A sub's Documents task with no documents yet (2026-10-24; server/workNeeded.js marks it docs_expected): flagged from
    its start date, until E3 Subs counts a document on it (`documents`: the task's count from tasks.progress, absent
    until E3 Subs sends it). */
export const needsDocs = (t, today, documents) => !!t.docs_expected && !!t.start_date
  && String(t.start_date).slice(0, 10) <= today && !(Number(documents) > 0);
const at = (ymd) => new Date(`${ymd}T12:00:00`);
/** 'Nov 14' this year, else 'Nov 14, 2025'. */
export const fmtShortDay = (ymd, today = todayYmd()) => at(ymd).toLocaleDateString('en-US', ymd.slice(0, 4) === today.slice(0, 4)
  ? { month: 'short', day: 'numeric' } : { month: 'short', day: 'numeric', year: 'numeric' });
/** 'Nov 14, 2026'. */
export const fmtDay = (ymd) => (ymd ? at(ymd).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '');
/** 'Jun 2027'. */
export const fmtMonthYear = (ymd) => at(ymd).toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
/** "Last job" (Duncan 2026-10-24: it showed scheduled work in 2027 as the last): "Now" while the sub is on a job
    today, else the last date that has come, else "Next <its first start>" when it only has work still to come. */
export const lastJobText = (s, fmt = fmtDay) => (s.on_job_now ? 'Now' : s.last_job ? fmt(s.last_job)
  : s.next_job ? `Next ${fmt(s.next_job)}` : '—');
/** The same for one sub's jobs (its page): { kind: 'now' | 'past' | 'next', date, job } or null. */
export function lastJobOf(jobs, today = todayYmd()) {
  const dates = (j) => [j.last_finish, j.first_start, ...(j.subcontracts || []).map((c) => c.contract_date)].filter(Boolean);
  const now = jobs.find((j) => j.first_start && j.first_start <= today && (j.last_finish || j.first_start) >= today);
  if (now) return { kind: 'now', date: today, job: now };
  const past = jobs.map((j) => ({ j, d: dates(j).filter((d) => d <= today).sort().pop() })).filter((x) => x.d)
    .sort((a, b) => b.d.localeCompare(a.d))[0];
  if (past) return { kind: 'past', date: past.d, job: past.j };
  const next = jobs.filter((j) => j.first_start && j.first_start > today).sort((a, b) => a.first_start.localeCompare(b.first_start))[0];
  return next ? { kind: 'next', date: next.first_start, job: next } : null;
}
const daysUntil = (ymd, today) => Math.round((Date.parse(`${ymd}T00:00:00Z`) - Date.parse(`${today}T00:00:00Z`)) / 86400000);

export const EXPIRING_DAYS = 30;

/** How an expiry date reads: red once lapsed, amber 30 days out, else plain. { text, tone }. */
export function expiryStatus(expiry, today = todayYmd()) {
  const left = daysUntil(expiry, today);
  if (left < 0) return { text: `expired ${fmtShortDay(expiry, today)}`, tone: 'var(--danger)', level: 'expired' };
  if (left <= EXPIRING_DAYS) return { text: left === 0 ? 'expires today' : `expires ${fmtShortDay(expiry, today)}`, tone: 'var(--warning)', level: 'expiring' };
  return { text: `to ${fmtMonthYear(expiry)}`, tone: null, level: 'ok' };
}

/** The list's MSA / Insurance cell for the current document (or `missing`): { text, tone }. */
export function docCell(doc, today = todayYmd(), missing = 'none on file') {
  if (!doc) return { text: missing, tone: 'var(--danger)' };
  if (doc.expiry_date) return expiryStatus(doc.expiry_date, today);
  const signed = doc.signed_date || doc.effective_date;
  return { text: signed ? `signed ${fmtMonthYear(signed)}` : 'on file', tone: null };
}

/* What every sub needs on file once (Duncan 2026-10-22: "we just get one" MSA and W-9, when they're set up): a
   missing one is flagged on its page and counted on the dashboard. Its certificate of insurance and safety plan
   are each job's (jobDocNeeds), not the company's. */
export const REQUIRED = [
  { key: 'msa', label: 'MSA' },
  { key: 'w9', label: 'W-9' },
];

/** Insurance from the current certificate's expiry: 'current' | 'expiring' (30 days) | 'expired' | 'none'. */
export function insuranceState(expiry, today = todayYmd()) {
  if (!expiry) return 'none';
  const level = expiryStatus(expiry, today).level;
  return level === 'ok' ? 'current' : level;
}

/** A list row's problems with its company documents: [{ key, text, tone }]. */
export function requiredIssues(sub) {
  const out = [];
  if (!sub.msa) out.push({ key: 'msa', text: 'No MSA', tone: 'var(--danger)' });
  if (!sub.w9) out.push({ key: 'w9', text: 'No W-9', tone: 'var(--warning)' });
  return out;
}

/** Where a sub's certificate for one job stands, from its documents for that job: { level, text, tone, doc,
    followUps }. level: 'none' | 'unnamed' (none names the job) | 'expired' | 'expiring' (30 days) | 'current'.
    A job can hold two certificates (one for E3, one for the owner): the one naming the job that runs latest
    sets the dates, and the follow-ups (additional insured, waiver of subrogation) count across those in force. */
export function coiState(docs, today = todayYmd()) {
  const cois = (docs || []).filter((d) => d.doc_type === 'insurance');
  if (!cois.length) return { level: 'none', text: 'No COI Recorded', tone: 'var(--danger)', doc: null, followUps: [] }; // Duncan 2026-10-21
  const naming = cois.filter((d) => d.names_job);
  if (!naming.length) return { level: 'unnamed', text: 'COI doesn’t name this job', tone: 'var(--danger)', doc: cois[0], followUps: [] };
  const best = naming.reduce((a, b) => (String(b.expiry_date || '') > String(a.expiry_date || '') ? b : a));
  const level = best.expiry_date ? expiryStatus(best.expiry_date, today).level : 'ok';
  if (level === 'expired') return { level: 'expired', text: `COI expired ${fmtShortDay(best.expiry_date, today)}`, tone: 'var(--danger)', doc: best, followUps: [] };
  const inForce = cois.filter((d) => !d.expiry_date || d.expiry_date >= today);
  const followUps = FOLLOW_UPS.filter((c) => !inForce.some((d) => d[c.key])).map((c) => c.short);
  if (level === 'expiring') return { level: 'expiring', text: `COI expires ${fmtShortDay(best.expiry_date, today)}`, tone: 'var(--warning)', doc: best, followUps };
  return { level: 'current', text: best.expiry_date ? `COI to ${fmtMonthYear(best.expiry_date)}` : 'COI on file', tone: 'var(--success)', doc: best, followUps };
}

/** A certificate's follow-ups as one line: "No waiver of subrogation", or "COI: 3 to follow up" (with the list in
    `title`). Null when there are none. */
export function followUpLine(followUps) {
  if (!followUps.length) return null;
  if (followUps.length === 1) return { text: `No ${followUps[0]}`, title: null };
  return { text: `COI: ${followUps.length} to follow up`, title: `Not on the certificate: ${followUps.join(', ')}` };
}

/** What a sub still owes a job (Duncan 2026-10-22): [{ key, text, tone, title }] -- its certificate (missing, not
    naming the job, lapsed or lapsing, then what to follow up on it) and its job site safety plan. */
export function jobDocNeeds(docs, today = todayYmd()) {
  const out = [];
  const coi = coiState(docs, today);
  if (coi.level !== 'current') out.push({ key: 'coi', text: coi.text, tone: coi.tone });
  const follow = followUpLine(coi.followUps);
  if (follow) out.push({ key: 'coi-follow', text: follow.text, tone: 'var(--warning)', title: follow.title });
  if (!(docs || []).some((d) => d.doc_type === 'safety')) out.push({ key: 'safety', text: 'No safety plan', tone: 'var(--danger)' });
  return out;
}

/** Where a flag takes you (Duncan 2026-10-22: "when you click on the flags of insurance, safety plan, and MSA it
    should take you to the spot where to file those things"): the Documents row that files it. */
export const focusOfNeed = (key) => (key === 'coi-follow' ? 'coi' : key);

/** How much of what a sub owes a job is in place: its certificate (naming the job, in force) and its safety plan.
    { have, of }. (Its MSA and W-9 are the company's, collected in Subcontractor Mgmt before work is assigned.) */
export function docsInPlace(docs, today = todayYmd()) {
  const coi = coiState(docs, today);
  const items = [coi.level === 'current' || coi.level === 'expiring', (docs || []).some((d) => d.doc_type === 'safety')];
  return { have: items.filter(Boolean).length, of: items.length };
}

/** The COI wording with a job's details in: {owner}, {job} and {number}. */
export const fillCoiWording = (template, job) => String(template || '')
  .replace(/\{owner\}/gi, (job && job.owner) || 'the owner')
  .replace(/\{job\}/gi, (job && job.name) || 'the job')
  .replace(/\{number\}/gi, (job && job.number) || '');

/** "Dec 7 → Aug 26, 2027": a run of dates, each in fmtShortDay's style. */
export const fmtSpan = (start, finish, today = todayYmd()) => [start, finish].filter(Boolean).map((d) => fmtShortDay(String(d).slice(0, 10), today)).join(' → ');

export const fmtSize = (b) => {
  if (b == null) return '';
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${Math.round(b / 1024)} KB`;
  return `${(b / (1024 * 1024)).toFixed(1)} MB`;
};
